// ============================================
// FILE: src/pages/ContactUs.jsx
// ✅ FIXED: Correct API Path (/user/contact-us)
// ============================================
import { useState } from 'react';
import { Mail, Phone, MapPin, Send, CheckCircle, Clock, AlertCircle } from 'lucide-react';
import api from '../services/api';

const ContactUs = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [status, setStatus] = useState({ type: '', message: '' });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus({ type: '', message: '' });

    try {
      // ✅ CORRECT PATH: 'handler.js' mounts register routes at '/user'
      const response = await api.post('/user/contact-us', formData);

      if (response.data.success) {
        setStatus({ type: 'success', message: 'Message sent successfully! We will get back to you shortly.' });
        setFormData({ name: '', email: '', subject: '', message: '' });
        setTimeout(() => setStatus({ type: '', message: '' }), 5000);
      }
    } catch (error) {
      console.error("Contact Error:", error);
      const errorMsg = error.response?.data?.message || "Failed to send message. Please try again.";
      setStatus({ type: 'error', message: errorMsg });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="py-8" style={{ minHeight: '100vh', background: '#f9fafb' }}>
      <div className="container">
        <div className="text-center mb-12">
          <h1 style={{ fontSize: '2.5rem', fontWeight: 700, marginBottom: '1rem', color: '#111827' }}>
            Get in Touch
          </h1>
          <p style={{ color: '#6b7280', fontSize: '1.125rem', maxWidth: '600px', margin: '0 auto' }}>
            We'd love to hear from you. Send us a message and we'll respond as soon as possible.
          </p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '2rem' }}>
          
          {/* LEFT: INFO CARDS */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            <div className="card" style={{ padding: '1.5rem', borderRadius: '12px', border: '1px solid #e5e7eb', background: 'white' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <div style={{ width: '56px', height: '56px', background: '#dbeafe', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Mail size={28} color="#2563eb" />
                </div>
                <div>
                  <h3 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '0.25rem' }}>Email Us</h3>
                  <a href="mailto:Support@smartdocs365.com" style={{ color: '#6b7280', textDecoration: 'none', fontSize: '1rem' }}>Support@smartdocs365.com</a>
                </div>
              </div>
            </div>

            <div className="card" style={{ padding: '1.5rem', borderRadius: '12px', border: '1px solid #e5e7eb', background: 'white' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <div style={{ width: '56px', height: '56px', background: '#d1fae5', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Phone size={28} color="#16a34a" />
                </div>
                <div>
                  <h3 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '0.25rem' }}>Call Us</h3>
                  <a href="tel:+918080737271" style={{ color: '#6b7280', textDecoration: 'none', fontSize: '1rem' }}>+91 8080737271</a>
                </div>
              </div>
            </div>

            <div className="card" style={{ padding: '1.5rem', borderRadius: '12px', border: '1px solid #e5e7eb', background: 'white' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <div style={{ width: '56px', height: '56px', background: '#ede9fe', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <MapPin size={28} color="#7c3aed" />
                </div>
                <div>
                  <h3 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '0.25rem' }}>Visit Us</h3>
                  <p style={{ color: '#6b7280', margin: 0, fontSize: '1rem' }}>502, Soni Apt, SVP Road, Borivali (W) Mumbai 400103</p>
                </div>
              </div>
            </div>

             <div className="card" style={{
              padding: '2rem',
              borderRadius: '12px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              boxShadow: '0 10px 15px -3px rgba(102, 126, 234, 0.3)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
                <Clock size={24} color="white" />
                <h3 style={{ fontSize: '1.25rem', fontWeight: 700, margin: 0 }}>Business Hours</h3>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', opacity: 0.9 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Mon - Fri:</span>
                  <span>9:00 AM - 6:00 PM IST</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Saturday:</span>
                  <span>10:00 AM - 4:00 PM IST</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Sunday:</span>
                  <span>Closed</span>
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT: CONTACT FORM */}
          <div className="card" style={{
            padding: '2rem',
            borderRadius: '12px',
            border: '1px solid #e5e7eb',
            background: 'white',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
          }}>
            <h2 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '1.5rem', color: '#111827' }}>
              Send us a Message
            </h2>

            {status.type === 'success' && (
              <div style={{ marginBottom: '1.5rem', padding: '1rem', background: '#dcfce7', border: '1px solid #86efac', borderRadius: '8px', color: '#166534', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <CheckCircle size={20} />
                <span>{status.message}</span>
              </div>
            )}

            {status.type === 'error' && (
              <div style={{ marginBottom: '1.5rem', padding: '1rem', background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: '8px', color: '#991b1b', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <AlertCircle size={20} />
                <span>{status.message}</span>
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div className="mb-4" style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500, color: '#374151' }}>Full Name</label>
                <input type="text" name="name" value={formData.name} onChange={handleChange} className="input-field" style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: '1px solid #d1d5db' }} required />
              </div>

              <div className="mb-4" style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500, color: '#374151' }}>Email Address</label>
                <input type="email" name="email" value={formData.email} onChange={handleChange} className="input-field" style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: '1px solid #d1d5db' }} required />
              </div>

              <div className="mb-4" style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500, color: '#374151' }}>Subject</label>
                <input type="text" name="subject" value={formData.subject} onChange={handleChange} className="input-field" style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: '1px solid #d1d5db' }} required />
              </div>

              <div className="mb-4" style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500, color: '#374151' }}>Message</label>
                <textarea name="message" value={formData.message} onChange={handleChange} className="input-field" rows={6} required style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: '1px solid #d1d5db', resize: 'vertical' }} />
              </div>

              <button type="submit" disabled={loading} className="btn btn-primary" style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', padding: '1rem', borderRadius: '8px', background: loading ? '#93c5fd' : '#2563eb', color: 'white', border: 'none', fontSize: '1rem', fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer' }}>
                {loading ? 'Sending...' : ( <> <Send size={20} /> Send Message </> )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactUs;